/**
 * AiModelPricingForm — create dialog body for `/admin/pricing/ai-models`.
 *
 * `unit` decide quais campos de preço aparecem: `token_1k` pede preço de
 * prompt + completion (chat), `audio_second` pede um único preço por
 * segundo (Whisper). Preço é sempre cadastro manual — nunca inferido.
 */

import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type AiModelPricingUnit = 'token_1k' | 'audio_second';

export interface AiModelPricingFormValue {
  provider: string;
  model: string;
  unit: AiModelPricingUnit;
  pricePromptPerUnit: number | null;
  priceCompletionPerUnit: number | null;
  pricePerUnit: number | null;
}

export const DEFAULT_AI_MODEL_PRICING_FORM: AiModelPricingFormValue = {
  provider: 'openai',
  model: '',
  unit: 'token_1k',
  pricePromptPerUnit: null,
  priceCompletionPerUnit: null,
  pricePerUnit: null,
};

interface Props {
  submitting?: boolean;
  onSubmit: (next: AiModelPricingFormValue) => void;
  onCancel: () => void;
}

export function AiModelPricingForm({ submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<AiModelPricingFormValue>(DEFAULT_AI_MODEL_PRICING_FORM);

  function patch<K extends keyof AiModelPricingFormValue>(
    key: K,
    value: AiModelPricingFormValue[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleUnitChange(next: AiModelPricingUnit) {
    setForm((prev) => ({
      ...prev,
      unit: next,
      pricePromptPerUnit: null,
      priceCompletionPerUnit: null,
      pricePerUnit: null,
    }));
  }

  const isValid =
    form.provider.trim().length > 0 &&
    form.model.trim().length > 0 &&
    (form.unit === 'token_1k'
      ? form.pricePromptPerUnit !== null && form.priceCompletionPerUnit !== null
      : form.pricePerUnit !== null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid) return;
    onSubmit(form);
  }

  const disabled = !isValid || !!submitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="ai-pricing-provider">Provider</Label>
          <Input
            id="ai-pricing-provider"
            aria-label="Provider"
            value={form.provider}
            onChange={(e) => patch('provider', e.target.value)}
            maxLength={20}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ai-pricing-model">Modelo</Label>
          <Input
            id="ai-pricing-model"
            aria-label="Modelo"
            value={form.model}
            onChange={(e) => patch('model', e.target.value)}
            placeholder="gpt-4o-mini"
            maxLength={50}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ai-pricing-unit">Unidade de cobrança</Label>
        <select
          id="ai-pricing-unit"
          aria-label="Unidade de cobrança"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={form.unit}
          onChange={(e) => handleUnitChange(e.target.value as AiModelPricingUnit)}
        >
          <option value="token_1k">token_1k (chat — prompt + completion)</option>
          <option value="audio_second">audio_second (Whisper)</option>
        </select>
      </div>

      {form.unit === 'token_1k' ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="ai-pricing-prompt">Preço prompt (USD / 1k tokens)</Label>
            <Input
              id="ai-pricing-prompt"
              aria-label="Preço prompt (USD / 1k tokens)"
              type="number"
              step={0.00001}
              min={0}
              value={form.pricePromptPerUnit ?? ''}
              onChange={(e) =>
                patch('pricePromptPerUnit', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ai-pricing-completion">Preço completion (USD / 1k tokens)</Label>
            <Input
              id="ai-pricing-completion"
              aria-label="Preço completion (USD / 1k tokens)"
              type="number"
              step={0.00001}
              min={0}
              value={form.priceCompletionPerUnit ?? ''}
              onChange={(e) =>
                patch(
                  'priceCompletionPerUnit',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="ai-pricing-per-second">Preço por segundo de áudio (USD)</Label>
          <Input
            id="ai-pricing-per-second"
            aria-label="Preço por segundo de áudio (USD)"
            type="number"
            step={0.00001}
            min={0}
            value={form.pricePerUnit ?? ''}
            onChange={(e) =>
              patch('pricePerUnit', e.target.value === '' ? null : Number(e.target.value))
            }
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Vale a partir de agora. Preço antigo não é sobrescrito — fica no histórico.
      </p>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={!!submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={disabled}>
          Cadastrar preço
        </Button>
      </div>
    </form>
  );
}
